import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, Calendar } from 'lucide-react';
import { contentAPI } from '../utils/api';
import { restaurantInfo as fallbackData } from '../utils/mockData';
import { openReservation } from '../utils/reservations';
import VisitInfo from './VisitInfo';

const Contact = () => {
  const [restaurantInfo, setRestaurantInfo] = useState(fallbackData);

  useEffect(() => {
    const fetchData = async () => {
      const data = await contentAPI.getRestaurantInfo();
      if (data && data.name) {
        // Map API data to expected format
        setRestaurantInfo({
          ...data,
          hours: {
            weekdays: data.hours_weekdays || fallbackData.hours.weekdays,
            weekends: data.hours_weekends || fallbackData.hours.weekends
          }
        });
      }
    };
    fetchData();
  }, []);

  const handleResyClick = () => {
    openReservation(null, 'home-contact');
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className="text-5xl md:text-6xl mb-6"
            style={{ fontFamily: 'var(--font-serif)' }}
          >
            Visit Us
          </h2>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Join us in the heart of LA's Arts District for an unforgettable dining experience
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div>
            <h3
              className="text-3xl mb-8"
              style={{ fontFamily: 'var(--font-serif)' }}
            >
              Get in Touch
            </h3>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-lg mb-1">Location</h4>
                  <p className="text-gray-700">{restaurantInfo.address}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone className="w-6 h-6 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-lg mb-1">Phone</h4>
                  <a
                    href={`tel:${restaurantInfo.phone}`}
                    className="text-gray-700 hover:underline"
                  >
                    {restaurantInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="w-6 h-6 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-lg mb-1">Email</h4>
                  <a
                    href={`mailto:${restaurantInfo.email}`}
                    className="text-gray-700 hover:underline"
                  >
                    {restaurantInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-lg mb-1">Hours</h4>
                  <p className="text-gray-700">
                    Wednesday - Sunday: {restaurantInfo.hours.weekdays}
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    Closed Monday &amp; Tuesday
                  </p>
                </div>
              </div>
            </div>

            {/* Google Maps */}
            <div className="mt-8 rounded-xl overflow-hidden shadow-lg">
              <iframe
                title="Brick Lane Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3305.8544193956394!2d-118.23677492346193!3d34.03862187315844!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2c7c5e1c5c5c5%3A0x5c5c5c5c5c5c5c5c!2s1331%20E%206th%20St%2C%20Los%20Angeles%2C%20CA%2090021!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              ></iframe>
            </div>
          </div>

          {/* Resy Reservation */}
          <div>
            <h3
              className="text-3xl mb-8"
              style={{ fontFamily: 'var(--font-serif)' }}
            >
              Make a Reservation
            </h3>

            <div className="bg-gradient-to-br from-[#F5EBE0] to-[#E8C9A0] rounded-xl p-8 shadow-lg">
              <div className="bg-white rounded-lg p-8">
                <div className="text-center mb-6">
                  <h4 className="text-2xl font-bold mb-3" style={{ fontFamily: 'var(--font-serif)' }}>
                    Book Your Table
                  </h4>
                  <p className="text-gray-700">
                    We use Resy for all reservations to provide you with the best booking experience
                  </p>
                </div>

                <div className="space-y-4">
                  <button
                    onClick={handleResyClick}
                    className="btn-primary w-full text-lg py-4 inline-flex items-center justify-center gap-2"
                  >
                    <Calendar size={18} /> RESERVE
                  </button>

                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">or</p>
                    <a
                      href={`tel:${restaurantInfo.phone}`}
                      className="text-[#B8956A] font-semibold hover:underline"
                    >
                      Call us: {restaurantInfo.phone}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm">
                Powered by <span className="font-semibold">Resy</span> - Your trusted reservation partner
              </p>
            </div>
          </div>
        </div>

        <VisitInfo />
      </div>
    </section>
  );
};

export default Contact;
